import type { StreamPoint } from './workoutStream';

const DURATIONS = [1, 5, 10, 15, 30, 60, 120, 180, 300, 360, 600, 720, 900, 1200, 1800, 2400, 3600, 5400, 7200, 10800];

/** Time-weighted peaks. Missing samples and pauses cannot create a valid window. */
export function selectedWorkoutPeaks(stream: StreamPoint[]) {
  const result: Record<string, Record<string, { interval: number; value: number; label: string; begin: number; end: number }>> = {};
  for (const [key, field, scale] of [
    ['pw_peak', 'power', 1], ['hr_peak', 'heartRate', 1],
    ['cd_peak', 'cadence', 1], ['sd_peak', 'speed', 3.6],
  ] as const) {
    const points = stream.filter((p, i) => Number.isFinite(p.timeSeconds) && (!i || p.timeSeconds > stream[i - 1].timeSeconds));
    const area = [0], covered = [0];
    for (let i = 0; i < points.length - 1; i++) {
      const dt = points[i + 1].timeSeconds - points[i].timeSeconds;
      const value = points[i][field];
      const valid = value != null && Number.isFinite(value) && !points[i].paused && dt <= 5;
      area.push(area[i] + (valid ? value * scale * dt : 0));
      covered.push(covered[i] + (valid ? dt : 0));
    }
    const integral = (time: number, sums: number[]) => {
      let lo = 0, hi = points.length - 1;
      while (lo < hi) { const mid = Math.ceil((lo + hi) / 2); if (points[mid].timeSeconds <= time) lo = mid; else hi = mid - 1; }
      if (lo === points.length - 1) return sums[lo];
      const dt = points[lo + 1].timeSeconds - points[lo].timeSeconds;
      return sums[lo] + (sums[lo + 1] - sums[lo]) * (time - points[lo].timeSeconds) / dt;
    };
    result[key] = {};
    if (points.length < 2) continue;
    const first = points[0].timeSeconds, last = points[points.length - 1].timeSeconds;
    for (const duration of DURATIONS) {
      if (duration > last - first) continue;
      let best: number | null = null;
      let bestStart = first;
      for (const point of points) for (const start of [point.timeSeconds, point.timeSeconds - duration]) {
        if (start < first || start + duration > last) continue;
        if (Math.abs(integral(start + duration, covered) - integral(start, covered) - duration) > 0.001) continue;
        const value = (integral(start + duration, area) - integral(start, area)) / duration;
        if (best == null || value > best) { best = value; bestStart = start; }
      }
      if (best != null) result[key][String(duration)] = { interval: duration, value: best, label: `${duration}s`, begin: bestStart, end: bestStart + duration };
    }
  }
  return result;
}
